import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { get } from '../utils/api';
import { queryKeys } from '../config/reactQuery';

interface EntitlementData {
  is_premium: boolean;
  is_cancelling: boolean;
  subscription_end_date: string | null;
  plan_type: string | null;
  is_student: boolean;
  is_corporate: boolean;
}

interface UseEntitlementResult {
  is_premium: boolean;
  is_cancelling: boolean;
  subscription_end_date: string | null;
  plan_type: string | null;
  is_student: boolean;
  is_corporate: boolean;
  loading: boolean;
  error: Error | null;
  entitlementError: null;
  refetch: () => void;
}

const ENTITLEMENT_STORAGE_KEY = 'wsle:entitlement';

interface CachedEntitlement extends EntitlementData {
  userId: string;
}

function getCachedEntitlement(userId: string): EntitlementData | undefined {
  try {
    const raw = localStorage.getItem(ENTITLEMENT_STORAGE_KEY);
    if (!raw) return undefined;
    const cached: CachedEntitlement = JSON.parse(raw);
    if (cached.userId !== userId) return undefined;
    // Skip cache if subscription has expired
    if (cached.subscription_end_date && new Date(cached.subscription_end_date) < new Date()) {
      return undefined;
    }
    return {
      is_premium: cached.is_premium,
      is_cancelling: cached.is_cancelling,
      subscription_end_date: cached.subscription_end_date,
      plan_type: cached.plan_type,
      is_student: cached.is_student ?? false,
      is_corporate: cached.is_corporate ?? false,
    };
  } catch {
    return undefined;
  }
}

function setCachedEntitlement(userId: string, data: EntitlementData): void {
  try {
    const cached: CachedEntitlement = { ...data, userId };
    localStorage.setItem(ENTITLEMENT_STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

function clearCachedEntitlement(): void {
  try {
    localStorage.removeItem(ENTITLEMENT_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook to fetch user entitlement status from the backend API.
 * Uses React Query with 5-min stale time to avoid unnecessary refetches.
 * Seeds initialData from localStorage for instant premium rendering on reload.
 */
export function useEntitlement(): UseEntitlementResult {
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => user?.id
      ? queryKeys.entitlement(user.id)
      : ['entitlement', 'anonymous'],
    [user?.id]
  );

  const fetchEntitlement = async (): Promise<EntitlementData> => {
    if (!user?.id) {
      return { is_premium: false, is_cancelling: false, subscription_end_date: null, plan_type: null, is_student: false, is_corporate: false };
    }

    const response = await get<EntitlementData>('/api/user/entitlement');
    return {
      is_premium: !!response.data?.is_premium,
      is_cancelling: !!response.data?.is_cancelling,
      subscription_end_date: response.data?.subscription_end_date ?? null,
      plan_type: response.data?.plan_type ?? null,
      is_student: !!response.data?.is_student,
      is_corporate: !!response.data?.is_corporate,
    };
  };

  // Seed from localStorage for instant rendering
  const cachedData = useMemo(
    () => user?.id ? getCachedEntitlement(user.id) : undefined,
    [user?.id]
  );

  const {
    data,
    isLoading,
    error,
    refetch: queryRefetch
  } = useQuery({
    queryKey,
    queryFn: fetchEntitlement,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    structuralSharing: true,
    retry: (failureCount) => failureCount < 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    placeholderData: undefined,
    notifyOnChangeProps: ['data', 'error', 'isLoading'],
    initialData: cachedData,
    // Mark cached data as stale so it is refreshed in the background on mount.
    // (Marking it 1 min old did nothing: with a 5 min staleTime it still counted as fresh.)
    initialDataUpdatedAt: cachedData ? 0 : undefined,
  });

  // Persist entitlement to localStorage on successful fetch
  useEffect(() => {
    if (data && user?.id) {
      setCachedEntitlement(user.id, data);
    }
  }, [data, user?.id]);

  // Invalidate cache on logout
  useEffect(() => {
    const handleLogout = () => {
      queryClient.invalidateQueries({ queryKey: ['entitlement'] });
      queryClient.removeQueries({ queryKey: ['entitlement'] });
      clearCachedEntitlement();
    };

    const handleAuthChange = () => {
      if (!user?.id) {
        handleLogout();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('wallstreetle-auth-logout', handleLogout);
      window.addEventListener('wallstreetle-auth-changed', handleAuthChange);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('wallstreetle-auth-logout', handleLogout);
        window.removeEventListener('wallstreetle-auth-changed', handleAuthChange);
      }
    };
  }, [queryClient, user?.id]);

  return {
    is_premium: data?.is_premium ?? false,
    is_cancelling: data?.is_cancelling ?? false,
    subscription_end_date: data?.subscription_end_date ?? null,
    plan_type: data?.plan_type ?? null,
    is_student: data?.is_student ?? false,
    is_corporate: data?.is_corporate ?? false,
    loading: isLoading,
    error: error as Error | null,
    entitlementError: null,
    refetch: () => queryRefetch()
  };
}
